/* eslint-disable import/no-extraneous-dependencies */
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink, Observable } from 'apollo-link';
import DebounceLink from 'apollo-link-debounce';
import { onError } from 'apollo-link-error';
import { HttpLink } from 'apollo-link-http';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import JwtDecode from 'jwt-decode';
import { ErrorSeverity, LoggerSDK } from '../containers/ErrorBoundary/TranquiLogger';
import { VersionErrorType } from '../generated/graphql';
import { refresh, session } from './auth';
import eventsTracking from './eventsTracking';
import { logout } from './logout';
import { isLocalStorageAvailable, storage } from './storage';

const cache = new InMemoryCache();

const TranquiLogger = new LoggerSDK();

const reloadsKey = `versionReloads_${process.env.REACT_APP_CLIENT_VERSION}`;

const requestLink = new ApolloLink((operation, forward) => new Observable((observer) => {
  let handle: any;
  Promise.resolve(operation)
    .then((op) => {
      const access = session.accessToken;
      if (!access) return;
      const customHeaders: any = {};
      customHeaders.latest_client_version = process.env.REACT_APP_CLIENT_VERSION;
      try {
        // send adminToke in each request if it exists
        const localAdminInfo = JSON.parse(storage.getItem('useLocalStorage_localAdminInfo') || '{}');
        if (localAdminInfo?.token) customHeaders.adminToken = localAdminInfo.token;
      } catch (e) {
        //
      }
      op.setContext({
        headers: {
          authorization: `Bearer ${access}`,
          ...customHeaders,
        },
      });
    })
    .then(() => {
      handle = forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        // complete: observer.complete.bind(observer),
        complete: (...args) => {
          const ctx = operation.getContext();
          const header = ctx.response.headers;
          const versionError = header.get('version_error');

          if (Object.values(VersionErrorType).includes(versionError)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            handleVersionError(versionError);
          } else {
            storage.setItem(reloadsKey, '0');
          }
          return observer.complete.bind(observer)(...args);
        },
      });
    })
    .catch(observer.error.bind(observer));

  return () => {
    if (handle) handle.unsubscribe();
  };
}));

const handleVersionError = (error: VersionErrorType) => {
  if (!isLocalStorageAvailable()) return;

  const reloads = parseInt(storage.getItem(reloadsKey) ?? '0', 10);

  switch (error) {
    case VersionErrorType.VersionMismatch:
      if (reloads <= 2) {
        if ('caches' in window) {
          caches.keys().then((names) => {
            // Delete cached files
            return Promise.allSettled(names.map((name) => {
              return caches.delete(name);
            }));
          }).then(() => {
            localStorage.setItem(reloadsKey, (reloads + 1).toString());
            window.location.reload();
          });
        }
      } else {
        // TranquiLogger.sendMessage({
        //   details: { error },
        //   message: 'Client version is outdated and it could not be reloaded in order to update.',
        // }, ErrorSeverity.error);
      }
      break;
    case VersionErrorType.NoClientVersion:
      TranquiLogger.sendMessage({
        details: { error },
        message: 'Current deployed client has no version in environment variables.',
      }, ErrorSeverity.error);
      break;
    default: {
      TranquiLogger.sendMessage({
        details: { error },
        message: 'Got an unexpected error verifying client version.',
      }, ErrorSeverity.error);
      break;
    }
  }
};

const tokenRefreshLink = new TokenRefreshLink({
  accessTokenField: 'access',
  isTokenValidOrUndefined: () => {
    const access = session.accessToken;
    if (!access) return true;
    try {
      const { exp } = JwtDecode(access) as { exp: number };
      return Date.now() < exp * 1000;
    } catch {
      return false;
    }
  },
  fetchAccessToken: refresh,
  handleFetch: (token) => {
    session.setAccessToken(token);
  },
  handleError: () => {
    logout();
  },
});

const debounceLink = new DebounceLink(200);

export const getApolloClient = (uri: string) => {
  return new ApolloClient({
    link: ApolloLink.from([
      debounceLink,
      tokenRefreshLink as any,
      onError(({ graphQLErrors, operation }) => {
        const { operationName, variables } = operation;
        if (graphQLErrors) {
          graphQLErrors.forEach((graphqlError) => {
            const { message, locations, path } = graphqlError;
            const idValOperations = [
              'ConfirmIdentityCCNumber',
              'ConfirmIdentityEmail', 'ConfirmIdentityAdmin', 'ConfirmIdentityDeepLink',
            ];
            // ignore auth required in known operations
            if (
              (message === 'auth required' || message === 'id validation required')
              && [
                'GetProfile', 'GetDebts', 'GetOutDebts', 'OutDebtsReport',
                'GetOutDebtNegotiationOptions', 'CreateNegotiation', 'newWaChatEvent',
                'GetIdentityConfirmationOptions', 'NewTwilioPhoneVerification', ...idValOperations,
              ].includes(operationName)) return;
            // ignore id validation errors
            if (/invalid (value|digits)/.test(message) && idValOperations.includes(operationName)) return;
            const errroMessage = {
              details: {
                operation: {
                  operationName,
                  variables,
                },
                error: graphqlError,
                enviroment: {
                  NODE_ENV: process.env.NODE_ENV,
                  REACT_APP_CLIENT_VERSION: process.env.REACT_APP_CLIENT_VERSION,
                  REACT_APP_ENV_OVERRIDE: process.env.REACT_APP_ENV_OVERRIDE,
                },
                route: window.location.href,
                userId: session.userId,
                uri,
              },
              message: 'App failed on graphql',
            };

            TranquiLogger.sendMessage(errroMessage, ErrorSeverity.error);
            // eslint-disable-next-line no-console
            console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

            const trackedErrors = [
              /cannot represent value/i,
              /cannot query field/i,
            ];
            if (trackedErrors.some((err) => err.test(message))) {
              eventsTracking.error.graphql(message);
            }
          });
        }
      }),
      requestLink,
      new HttpLink({ uri, credentials: 'include' }),
    ]),
    cache,
  });
};
